import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { InspectionSkipReason } from '@raptor-fe/core';
import { Observable } from 'rxjs';
import { ServiceUtils } from '../service-utils/service-utils';

/**
 * Tenant-admin client for the Digital Vehicle Inspection skip-reason catalog. Targets the
 * tenant-scoped `InspectionSkipReasonController` at `api/v1/tenant/{tenantId}/InspectionSkipReason`, gated
 * server-side by the DigitalVehicleInspection + SkipInspection feature flags and the
 * InspectionTemplate admin permissions. URLs are built through {@link ServiceUtils.getBaseUrl}
 * so the tenant segment is constructed consistently with the rest of the tenant-scoped API.
 *
 * Branch scoping lives in the reason body (a null/empty `branchId` is a tenant-level reason;
 * a real id scopes it to that branch). The admin list endpoint returns every reason across all
 * scopes and both active and inactive states, so scope filtering for the UI is performed
 * client-side in the store rather than by the server.
 */
@Injectable({ providedIn: 'root' })
export class SkipReasonAdminService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);

    /**
     * Lists every skip reason for the tenant (all scopes, active and inactive) for administrative
     * management. The optional `branchId` is forwarded as a query parameter for forward
     * compatibility; the current server contract ignores it and returns all scopes, so the store
     * remains responsible for the authoritative client-side scope filter.
     *
     * @param tenantId The tenant whose catalog is listed.
     * @param branchId Optional branch scope hint; forwarded when supplied.
     * @returns An observable of the tenant's skip reasons.
     */
    getSkipReasonsAdmin(tenantId: string, branchId?: string | null): Observable<InspectionSkipReason[]> {
        const url = this.serviceUtils.getBaseUrl('InspectionSkipReason', tenantId);
        return this.http.get<InspectionSkipReason[]>(url, branchId ? { params: { branchId } } : undefined);
    }

    /**
     * Creates (POST) or updates (PUT) a skip reason. The supplied `branchId` is stamped onto the
     * request body as the reason's scope (null/empty for a tenant-level reason), overriding any
     * branch already on the reason so the store's selected scope is authoritative.
     *
     * @param tenantId The tenant that owns the reason.
     * @param branchId The scope to persist: null/empty for tenant-level, a branch id otherwise.
     * @param reason The reason payload (code, name, isActive, and, on update, id).
     * @param isNew True to create, false to update.
     * @returns An observable of the persisted reason.
     */
    saveSkipReason(tenantId: string, branchId: string | null, reason: InspectionSkipReason, isNew: boolean): Observable<InspectionSkipReason> {
        const url = this.serviceUtils.getBaseUrl('InspectionSkipReason', tenantId);
        const body: InspectionSkipReason = { ...reason, branchId: branchId ?? null };
        return isNew ? this.http.post<InspectionSkipReason>(url, body) : this.http.put<InspectionSkipReason>(url, body);
    }

    /**
     * Soft-deletes (retires) a skip reason. Branch authorization for a branch-scoped reason is
     * resolved server-side from the stored reason, so `branchId` is accepted for call-site scope
     * symmetry but is not part of the delete route.
     *
     * @param tenantId The tenant that owns the reason.
     * @param branchId The scope the caller is acting within; not sent on the request.
     * @param id The identifier of the reason to retire.
     * @returns An observable that completes when the reason is retired.
     */
    deleteSkipReason(tenantId: string, branchId: string | null, id: string): Observable<void> {
        const url = `${this.serviceUtils.getBaseUrl('InspectionSkipReason', tenantId)}/${id}`;
        return this.http.delete<void>(url);
    }
}
